import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
type UTMKey = typeof UTM_KEYS[number];

export interface UTMData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  landing_page?: string;
  captured_at?: string;
}

const STORAGE_KEY = 'boostio_utm';

/**
 * Runs inside BrowserRouter - detects UTM params in URL and persists them in sessionStorage.
 * Only overwrites stored UTMs when new ones are present in the URL.
 */
export function useUTMCapture(): void {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const data: UTMData = {};
    let hasAny = false;

    UTM_KEYS.forEach((key: UTMKey) => {
      const val = params.get(key);
      if (val) {
        data[key] = val;
        hasAny = true;
      }
    });

    if (hasAny) {
      data.landing_page = location.pathname;
      data.captured_at = new Date().toISOString();
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch {
        // sessionStorage not available
      }
    }
  }, [location.search, location.pathname]);
}

/**
 * Returns stored UTM data from sessionStorage.
 * Safe to call anywhere - returns empty object if nothing is stored.
 */
export function getStoredUTM(): UTMData {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as UTMData) : {};
  } catch {
    return {};
  }
}

/**
 * Converts UTMData to URLSearchParams-compatible flat record.
 * Only includes keys that have a value.
 */
export function utmToParams(utm: UTMData): Record<string, string> {
  const result: Record<string, string> = {};
  (Object.keys(utm) as Array<keyof UTMData>).forEach((key) => {
    const val = utm[key];
    if (val) result[key] = val;
  });
  return result;
}
